import { IpaChartProps } from "./IpaChart";
import { IpaRow } from "./Ipa";

interface ConsonantChartProps extends IpaChartProps {
  /** include just (ejective-plosives & clicks) or all non-pulmonics (clicks, implosives, ejectives): default none */
  readonly includeNonPulmonics?: 'special' | 'nonPulmonics' | 'none';
}

/** show IPA consonants chart */
export function ConsonantChart(props: ConsonantChartProps) {
  // pulmonic
  const plosives = [['p', 'b'], ['p̪', 'b̪'], ['kp', 'ɡb'], ['t̪', 'd̪'], ['t', 'd'], ['t̠', 'd̠'], ['ʈ', 'ɖ'], ['ȶ', ''], ['c', 'ɟ'], ['k', 'ɡ'], ['q', 'ɢ'], ['ʡ', ''], ['ʔ', '']];
  const nasals = [['m̥', 'm'], ['ɱ̥', 'ɱ'], ['', 'ŋm'], ['', 'n̪'], ['n̥', 'n'], ['', 'n̠'], ['ɳ̥', 'ɳ'], ['', 'ȵ'], ['ɲ̥', 'ɲ'], ['ŋ̥', 'ŋ'], ['', 'ɴ'], [], []];
  const trills = [['', 'ʙ'], [], [], ['', 'r̪'], ['r̥', 'r'], ['', 'r̠'], [], [], [], [], ['ʀ̥', 'ʀ'], ['ʜ', 'ʢ'], []];
  const taps = [['', 'ⱱ̟'], ['', 'ⱱ'], [], ['', 'ɾ̪'], ['ɾ̥', 'ɾ'], [], ['ɽ̊', 'ɽ'], [], [], [], ['', 'ɢ̆'], [], []];
  const affricates = [['pɸ', 'bβ'], ['pf', 'bv'], [], ['t̪θ', 'd̪ð'], ['ts', 'dz'], ['t̠ʃ', 'd̠ʒ'], ['ʈʂ', 'ɖʐ'], ['tɕ', 'dʑ'], ['cç', 'ɟʝ'], ['kx', 'ɡɣ'], ['qχ', 'ɢʁ'], ['ʡʜ', 'ʡʢ'], ['ʔh', '']];
  const fricatives = [['ɸ', 'β'], ['f', 'v'], ['ʍ', ''], ['θ', 'ð'], ['s', 'z'], ['ʃ', 'ʒ'], ['ʂ', 'ʐ'], ['ɕ', 'ʑ'], ['ç', 'ʝ'], ['x', 'ɣ'], ['χ', 'ʁ'], ['ħ', 'ʕ'], ['h', 'ɦ']];
  const latfric = [[], [], [], ['ɬ̪', 'ɮ̪'], ['ɬ', 'ɮ'], [], ['ꞎ', ''], [], [], [/*'𝼄', ''*/], [], [], []];
  const approx = [['', 'β̞'], ['', 'ʋ'], ['', 'w'], ['', 'ð̞'], ['', 'ɹ'], [], ['', 'ɻ'], [], ['', 'j'], ['', 'ɰ'], ['', 'ʁ̞'], [], []];
  const latapprox = [[], [], [], ['', 'l̪'], ['l̥', 'l'], [], ['', 'ɭ'], ['', 'ȴ'], ['', 'ʎ'], ['', 'ʟ'], [], [], []];
  // non-pulmonic
  const ejectivePlosive = [['pʼ', ''], [], [], ['t̪ʼ', ''], ['tʼ', ''], [], ['ʈʼ', ''], [], ['cʼ', ''], ['kʼ', ''], ['qʼ', ''], ['', ''], ['', 'ʔʼ']];
  const ejectiveFricative = [['ɸʼ', ''], ['fʼ', ''], ['', 'wʼ'], ['θʼ', 'ðʼ'], ['sʼ', 'zʼ'], ['ʃʼ', 'ʒʼ'], ['ʂʼ', ''], ['ɕʼ', ''], ['çʼ', ''], ['xʼ', ''], ['χʼ', ''], ['', 'ʕʼ'], ['hʼ', '']];
  const implosives = [['ɓ̥', 'ɓ'], [], ['ɠ̊ɓ̥', 'ɠɓ'], [], ['ɗ̥', 'ɗ'], [], ['', 'ᶑ'], [], ['ʄ̥', 'ʄ'], ['ɠ̥', 'ɠ'], ['ʛ̥', 'ʛ'], [], []];
  const clicks = [['ʘ', ''], [], [], ['ǀ', ''], ['ǃ', ''], ['ǁ', ''], ['‼', ''], [], ['ǂ', ''], [], [], [], []];
  return (
    <table>
      <thead>
        <tr>
          <td></td>
          <th>Bilabial</th>
          <th>Labio<br />dental</th>
          <th>Labial<br />Velar</th>
          <th>Dental</th>
          <th>Alveolar</th>
          <th>Post<br />alveolar</th>
          <th>Retro<br />flex</th>
          <th>Alveolo<br />palatal</th>
          <th>Palatal</th>
          <th>Velar</th>
          <th>Uvular</th>
          <th>Pharyn<br />geal</th>
          <th>Glottal</th>
        </tr>
      </thead>
      <tbody>
        <IpaRow title='Plosive' symbols={plosives} chartProps={props} />
        <IpaRow title='Nasal' symbols={nasals} chartProps={props} />
        <IpaRow title='Trill' symbols={trills} chartProps={props} />
        <IpaRow title='Tap or Flap' symbols={taps} chartProps={props} />
        <IpaRow title='Affricate' symbols={affricates} chartProps={props} />
        <IpaRow title='Fricative' symbols={fricatives} chartProps={props} />
        <IpaRow title='Lateral fricative' symbols={latfric} chartProps={props} />
        <IpaRow title='Approximant' symbols={approx} chartProps={props} />
        <IpaRow title='Lateral approximant' symbols={latapprox} chartProps={props} />
        {props.includeNonPulmonics === undefined || props.includeNonPulmonics === 'none' ? <></>
          : props.includeNonPulmonics === 'special'
            ? <>
              <IpaRow title='Ejective plosive' symbols={ejectivePlosive} chartProps={props} />
            </>
            : <>
              <tr><th>- NONPULMONIC -</th></tr>
              <IpaRow title='Ejective plosive' symbols={ejectivePlosive} chartProps={props} />
              <IpaRow title='Ejective fricative' symbols={ejectiveFricative} chartProps={props} />
              <IpaRow title='Implosive' symbols={implosives} chartProps={props} />
              <IpaRow title='Click' symbols={clicks} chartProps={props} />
            </>}
      </tbody>
    </table>
  );
}
